import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Checkbox,
    Text,
    VStack,
    HStack,
    Box,
    useToast,
    Icon,
} from "@chakra-ui/react";
import {
    IoStorefrontOutline,
    IoArrowForward,
    IoDocumentTextOutline,
    IoOpenOutline,
} from "react-icons/io5";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/firebase-config";
import { useEffect, useState } from "react";
import venueAgreementPdf from "../../Assets/PDF/Proximy_Venue_Platform_and_Event_Hosting_Agreement_Draft.pdf";
import { VENUE_AGREEMENT_VERSION, VENUE_AGREEMENT_NAME } from "../../lib/venueAgreement";

// Gate in front of the venue creation form — a venue partner must sign the
// Venue Platform and Event Hosting Agreement once before they can submit
// their first (or any future) venue.
export const VenueAgreementModal = ({ isOpen, onClose, user, onSigned }) => {
    const [loading, setLoading] = useState(false);
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (isOpen) {
            setAgreementAccepted(false);
            setShowAgreement(false);
        }
    }, [isOpen]);

    const handleSign = async () => {
        if (!user) return;
        if (!agreementAccepted) {
            toast({
                title: "Agreement required",
                description:
                    "Please review and agree to the Venue Platform and Event Hosting Agreement before continuing.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);
        try {
            const callable = httpsCallable(functions, "recordVenueAgreementConsent");
            const { data: result } = await callable({
                userId: user.uid,
                email: user.email,
                appBaseUrl: window.location.origin,
                agreementConsent: {
                    accepted: true,
                    agreementVersion: VENUE_AGREEMENT_VERSION,
                    agreementName: VENUE_AGREEMENT_NAME,
                    agreementUrl: venueAgreementPdf,
                    acceptedAtClient: new Date().toISOString(),
                },
            });

            if (result?.success) {
                toast({
                    title: "Agreement signed",
                    status: "success",
                    duration: 2500,
                    isClosable: true,
                });
                onSigned?.();
            } else {
                throw new Error("Failed to record agreement consent");
            }
        } catch (error) {
            console.error("❌ Error signing venue agreement:", error);
            toast({
                title: "Error",
                description:
                    error.message || "Failed to sign the agreement. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={{ base: "full", md: "md" }}
            isCentered
            scrollBehavior="inside"
        >
            <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
            <ModalContent
                borderRadius={{ base: 0, md: "2xl" }}
                overflow="hidden"
            >
                <ModalHeader
                    pb={2}
                    pt={5}
                    px={6}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                >
                    <HStack spacing={3} align="center">
                        <Box
                            bg="blue.50"
                            p={2}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon
                                as={IoStorefrontOutline}
                                color="#508cf7"
                                boxSize={4}
                            />
                        </Box>
                        <Box>
                            <Text
                                fontSize="lg"
                                fontWeight="700"
                                color="gray.800"
                            >
                                Venue Partner Agreement
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                                One last step before you list your venue
                            </Text>
                        </Box>
                    </HStack>
                    <ModalCloseButton top={4} right={4} />
                </ModalHeader>

                <ModalBody px={6} py={5}>
                    <VStack spacing={5} align="stretch">
                        <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                            Before you can create a venue listing, please review and
                            agree to the Venue Platform and Event Hosting Agreement.
                            It covers how bookings, payouts, and responsibilities work
                            between you and Proximy.
                        </Text>

                        <Box
                            border="1px solid"
                            borderColor={agreementAccepted ? "blue.200" : "gray.200"}
                            borderRadius="xl"
                            p={4}
                            bg={agreementAccepted ? "blue.50" : "white"}
                        >
                            <HStack align="flex-start" spacing={3}>
                                <Checkbox
                                    isChecked={agreementAccepted}
                                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                                    colorScheme="blue"
                                    mt="2px"
                                    isDisabled={loading}
                                />
                                <Box flex={1}>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                        I agree to the Venue Platform and Event Hosting Agreement
                                    </Text>
                                    <HStack spacing={3} mt={2} flexWrap="wrap">
                                        <Button
                                            size="xs"
                                            variant="link"
                                            color="#508cf7"
                                            fontWeight="700"
                                            leftIcon={<Icon as={IoDocumentTextOutline} boxSize={3.5} />}
                                            onClick={() => setShowAgreement((prev) => !prev)}
                                        >
                                            {showAgreement ? "Hide agreement" : "View agreement"}
                                        </Button>
                                        <Button
                                            as="a"
                                            href={venueAgreementPdf}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="xs"
                                            variant="link"
                                            color="gray.500"
                                            fontWeight="600"
                                            leftIcon={<Icon as={IoOpenOutline} boxSize={3.5} />}
                                        >
                                            Open PDF
                                        </Button>
                                    </HStack>
                                </Box>
                            </HStack>

                            {showAgreement && (
                                <Box
                                    mt={4}
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="lg"
                                    overflow="hidden"
                                    bg="gray.50"
                                    h={{ base: "360px", md: "420px" }}
                                >
                                    <Box
                                        as="iframe"
                                        title="Venue Platform and Event Hosting Agreement"
                                        src={venueAgreementPdf}
                                        w="100%"
                                        h="100%"
                                        border="0"
                                    />
                                </Box>
                            )}
                        </Box>

                        <Text
                            fontSize="11px"
                            color="gray.400"
                            textAlign="center"
                        >
                            You only need to sign this once — it covers all of your
                            venue listings.
                        </Text>
                    </VStack>
                </ModalBody>

                <ModalFooter
                    borderTop="1px solid"
                    borderColor="gray.100"
                    py={4}
                    px={6}
                >
                    <Button
                        size="sm"
                        variant="ghost"
                        fontWeight="600"
                        onClick={onClose}
                        mr={2}
                        isDisabled={loading}
                    >
                        Not now
                    </Button>
                    <Button
                        size="sm"
                        bg="#508cf7"
                        color="white"
                        fontWeight="600"
                        borderRadius="lg"
                        px={5}
                        rightIcon={<Icon as={IoArrowForward} boxSize={4} />}
                        _hover={{ bg: "#226cf1" }}
                        onClick={handleSign}
                        isLoading={loading}
                        isDisabled={!agreementAccepted}
                        loadingText="Signing…"
                    >
                        Agree & Continue
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
